import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useScroll, useTransform, motion } from 'framer-motion';
import { useLenis } from '@/hooks/useLenis';
import { FadeSection } from '@/components/shared/FadeSection';
import '@/styles/private.css';

const PrivatePage = () => {
  useLenis();
  
  const imageRef = useRef(null);
  const videoRef = useRef(null);
  const videoSectionRef = useRef(null);
  const prioritySectionRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);
  
  const { scrollYProgress } = useScroll({
    target: imageRef,
    offset: ["start end", "end start"]
  });
  
  const { scrollYProgress: videoScrollProgress } = useScroll({
    target: videoSectionRef,
    offset: ["start end", "end start"]
  });

  // Priority section fade out as video enters
  const { scrollYProgress: priorityFadeProgress } = useScroll({
    target: prioritySectionRef,
    offset: ["end end", "end start"]
  });
  
  const imageY = useTransform(scrollYProgress, [0, 1], ['-5%', '5%']);
  const imageScale = useTransform(scrollYProgress, [0, 0.5, 1], [1, 1.02, 1.03]);
  
  // Video parallax, scale, blur and contrast
  const videoY = useTransform(videoScrollProgress, [0, 1], ['-3%', '3%']);
  const videoScale = useTransform(videoScrollProgress, [0, 0.3, 1], [1.08, 1.05, 1.03]);
  const videoBlur = useTransform(videoScrollProgress, [0, 0.25, 0.5], [2, 0.5, 0]);
  const videoBrightness = useTransform(videoScrollProgress, [0, 0.3, 0.6], [0.55, 0.65, 0.7]);
  
  // Priority section opacity fade
  const priorityOpacity = useTransform(priorityFadeProgress, [0, 0.8, 1], [1, 1, 0.3]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.playbackRate = 0.85;
      
      const handleCanPlay = () => {
        setVideoReady(true);
      };
      
      video.addEventListener('canplaythrough', handleCanPlay);
      
      video.play().catch(() => {
        const playOnInteraction = () => {
          video.play();
          document.removeEventListener('click', playOnInteraction);
        };
        document.addEventListener('click', playOnInteraction);
      });
      
      return () => {
        video.removeEventListener('canplaythrough', handleCanPlay);
      };
    }
  }, []);

  const designFeatures = [
    {
      title: "Disappearing Images",
      desc: "Images can be shared intentionally and don't remain permanently."
    },
    {
      title: "Read Receipts",
      desc: "See when messages are read. No more wondering."
    },
    {
      title: "Video Messages",
      desc: "Short video messages for more real, human interaction."
    },
    {
      title: "Controlled Access",
      desc: "Grant and revoke access to private content at any time."
    },
    {
      title: "A Quieter Layer",
      desc: "Less noise. More intentional interactions."
    }
  ];

  return (
    <div className="private-page" data-testid="private-page">
      <div className="noise-overlay" />
      
      {/* Hero Section */}
      <section className="private-hero" data-testid="private-hero">
        <div className="private-hero-inner">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="private-hero-headline"
          >
            NYMPHÉ Private
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="private-hero-sub"
          >
            Not everything is visible.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
          >
            <Link to="/#signup" className="private-hero-cta" data-testid="private-hero-cta">
              Join waitlist
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Intro Section with Visual */}
      <section className="private-intro" data-testid="private-intro">
        <div className="private-intro-split">
          <div ref={imageRef} className="private-intro-visual">
            <motion.div 
              className="private-intro-image"
              style={{ 
                backgroundImage: `url(${process.env.PUBLIC_URL}/editorial-2.jpg)`,
                y: imageY,
                scale: imageScale
              }}
            />
            <div className="private-intro-image-overlay" />
          </div>
          <div className="private-intro-text">
            <FadeSection>
              <p className="private-intro-line">
                Private changes how you're seen.
              </p>
            </FadeSection>
            <FadeSection delay={0.1}>
              <p className="private-intro-line private-intro-line-secondary">
                And who gets to see you.
              </p>
            </FadeSection>
          </div>
        </div>
      </section>

      {/* Feature: Private Galleries */}
      <section className="private-feature" data-testid="private-galleries">
        <div className="private-feature-inner">
          <FadeSection>
            <p className="private-feature-label">Private Galleries</p>
          </FadeSection>
          <FadeSection delay={0.1}>
            <h2 className="private-feature-headline">
              Share only when you choose.
            </h2>
          </FadeSection>
          <FadeSection delay={0.2}>
            <p className="private-feature-body">
              Your private photos stay yours. 
              You decide who sees them, and you can take that back anytime.
            </p>
          </FadeSection>
        </div>
      </section>

      {/* Feature: Intent & Visibility */}
      <section className="private-feature private-feature-alt" data-testid="private-visibility">
        <div className="private-feature-inner">
          <FadeSection>
            <p className="private-feature-label">Intent & Visibility</p>
          </FadeSection>
          <FadeSection delay={0.1}>
            <h2 className="private-feature-headline">
              Know who's actually interested.
            </h2>
          </FadeSection>
          <FadeSection delay={0.2}>
            <p className="private-feature-body">
              See who's viewed you. Know who wants to talk before you reach out. 
              No more wondering.
            </p>
          </FadeSection>
        </div>
      </section>

      {/* Feature: Advanced Filters */}
      <section className="private-feature" data-testid="private-filters">
        <div className="private-feature-inner">
          <FadeSection>
            <p className="private-feature-label">Advanced Filters</p>
          </FadeSection>
          <FadeSection delay={0.1}>
            <h2 className="private-feature-headline">
              Find exactly what you're drawn to.
            </h2>
          </FadeSection>
          <FadeSection delay={0.2}>
            <p className="private-feature-body">
              Search the way you want. 
              Your criteria, your preferences, no compromises.
            </p>
          </FadeSection>
        </div>
      </section>

      {/* Feature: Priority Experience */}
      <motion.section 
        ref={prioritySectionRef}
        className="private-feature private-feature-alt" 
        data-testid="private-priority"
        style={{ opacity: priorityOpacity }}
      >
        <div className="private-feature-inner">
          <FadeSection>
            <p className="private-feature-label">Priority Experience</p>
          </FadeSection>
          <FadeSection delay={0.1}>
            <h2 className="private-feature-headline">
              Be seen by the people who matter.
            </h2>
          </FadeSection>
          <FadeSection delay={0.2}>
            <p className="private-feature-body">
              More visibility where it counts. Fewer barriers. 
              The experience you want, without the noise.
            </p>
          </FadeSection>
        </div>
      </motion.section>

      {/* Atmospheric Video Break */}
      <section ref={videoSectionRef} className="private-video-break" data-testid="private-video">
        <div className="private-video-gradient-top" />
        
        {/* Poster Image Fallback */}
        <motion.div 
          className="private-video-poster"
          style={{ 
            y: videoY, 
            scale: videoScale,
            filter: useTransform(
              [videoBlur, videoBrightness],
              ([blur, brightness]) => `blur(${blur}px) brightness(${brightness}) saturate(0.85)`
            )
          }}
        >
          <div 
            className="private-video-poster-image"
            style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/editorial-2.jpg)` }}
          />
        </motion.div>
        
        {/* Video Layer */}
        <motion.div 
          className={`private-video-container ${videoReady ? 'video-ready' : ''}`}
          style={{ 
            y: videoY, 
            scale: videoScale,
            filter: useTransform(
              [videoBlur, videoBrightness],
              ([blur, brightness]) => `blur(${blur}px) brightness(${brightness}) saturate(0.85)`
            )
          }}
        >
          <video
            ref={videoRef}
            className="private-video"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            src="/private-video.mov"
          />
        </motion.div>
        
        <div className="private-video-overlay" />
      </section>

      {/* Private, by design */}
      <section className="private-bydesign" data-testid="private-bydesign">
        <div className="private-bydesign-inner">
          <FadeSection>
            <h2 className="private-bydesign-title">Private, by design</h2>
          </FadeSection>
          
          <div className="private-bydesign-grid">
            {designFeatures.map((feature, index) => (
              <FadeSection key={index} delay={0.1 + index * 0.06} className="private-bydesign-item">
                <h3 className="private-bydesign-item-title">{feature.title}</h3>
                <p className="private-bydesign-item-desc">{feature.desc}</p>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* Tension + Closing CTA */}
      <section className="private-cta" data-testid="private-cta">
        <div className="private-cta-inner">
          <div className="private-cta-tension-group">
            <FadeSection>
              <p className="private-cta-tension">
                Not everything should be visible.
              </p>
            </FadeSection>
            <FadeSection delay={0.08}>
              <p className="private-cta-tension private-cta-tension-second">
                And not everyone should have access.
              </p>
            </FadeSection>
          </div>
          <div className="private-cta-action-group">
            <FadeSection delay={0.25}>
              <Link to="/#signup" className="private-cta-btn" data-testid="private-cta-btn">
                Join waitlist
              </Link>
            </FadeSection>
            <FadeSection delay={0.35}>
              <p className="private-cta-sub">
                Access changes the experience.
              </p>
            </FadeSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivatePage;
